import { isEmpty, omit } from 'lodash';
import moment from 'moment';
import { useRouter } from 'next/router';
import React, { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import URI from 'urijs';
import { url as urlUtils } from '@clubmed/utils';
import { Button } from 'Components/frameworks/button/Button';
import { Countdown } from 'Components/frameworks/countdown/Countdown';
import { Modal } from 'Components/frameworks/modal/Modal';
import { COLORS } from 'Components/utils/color/colors';
import type { InfoBannerData } from 'Components/widgets/info-banner/infoBanner.query';
import { getInfoBannerQuery } from 'Components/widgets/info-banner/infoBanner.query';
import { useAppContext } from 'Context/ReactAppContext';
import { t } from 'Src/i18n';
import { getInsurancesStore } from 'Store/booking/booking.selector';
import { INIT_INSURANCES } from 'Store/booking/proposals/constants';
import createAction from 'Store/utils/createAction';
import graphqlClient from 'Utils/graphql-client/graphqlClient';
import { isChineseLocale } from 'Utils/locale/localeUtils';
import { WIDGET_PRODUCT_FETCH_INFORMATION_BANNERS } from 'Utils/request/requestDictionary';
import {
  get as getTrackingStorage,
  set as setTrackingStorage,
} from 'Tracking/tracking-storage-api';
import { BE_TRACKING } from 'Tracking/constants';
import { AccommodationView } from './accommodation/AccommodationView';
import { ActivitiesView } from './activities/ActivitiesView';
import { BankingView } from './banking/BankingView';
import { ParticipantsView } from './participants/ParticipantsView';
import ServicesView from './services/ServicesView';
import SummaryView from './summary/SummaryView';
import { TransportsView } from './transport/TransportsView';
type Props = {
  actionGA: string;
  closeLayer: (arg0: boolean | null | undefined, arg1: string | null | undefined) => void;
  currentStep: string;
  goToNextStep: (params?: { queryParams: Record<string, string> }) => Promise<unknown>;
  i18n: I18N;
  openLayer: (arg0: string) => void;
  proposals: Array<Proposal>;
  resort: ProductWithAccommodations;
  selectedProposal: Proposal;
  streetViews: Array<StreetViewWithAccommodationCategoryId>;
  isOptionHoldOnly: boolean;
  isFavorite: boolean;
  attendeesPaymentStatus: Array<AttendeePaymentStatus>;
};
type BookingStepContentProps = Props & {
  showTimer: boolean;
  timerInitialValue: number;
  setTimerInitialValue: (arg0: number) => void;
  infoBannerData: InfoBannerData;
};
export const getChinaTimerSessionKey = (id: string) => `china-summary-timer_${id}`;

const BookingStepContent = ({
  actionGA,
  closeLayer,
  currentStep,
  goToNextStep,
  i18n,
  isFavorite,
  openLayer,
  proposals,
  resort,
  selectedProposal,
  setTimerInitialValue,
  showTimer,
  streetViews,
  timerInitialValue,
  infoBannerData,
  isOptionHoldOnly,
  attendeesPaymentStatus,
}: BookingStepContentProps) => {
  const sharedProps = {
    proposals,
    selectedProposal,
    resort,
    infoBannerData,
  };

  switch (currentStep) {
    case 'accommodation':
      return (
        <AccommodationView
          {...sharedProps}
          closeLayer={closeLayer}
          openLayer={openLayer}
          streetViews={streetViews}
        />
      );

    case 'transport':
      return (
        <TransportsView
          {...sharedProps}
          closeLayer={closeLayer}
          i18n={i18n}
          openLayer={openLayer}
        />
      );

    case 'participants':
      return (
        <ParticipantsView
          {...sharedProps}
          closeLayer={closeLayer}
          goToNextStep={goToNextStep}
          i18n={i18n}
          openLayer={openLayer}
        />
      );

    case 'summary':
      return (
        <SummaryView
          {...sharedProps}
          actionGA={actionGA}
          bookingStep={currentStep}
          goToNextStep={goToNextStep}
          i18n={i18n}
          isFavorite={isFavorite}
          showTimer={showTimer && !timerInitialValue}
          setTimerInitialValue={setTimerInitialValue}
          isOptionHoldOnly={isOptionHoldOnly}
        />
      );

    case 'banking':
      return (
        <BankingView {...sharedProps} i18n={i18n} attendeesPaymentStatus={attendeesPaymentStatus} />
      );

    case 'services':
      return <ServicesView {...sharedProps} />;

    case 'activities':
      return <ActivitiesView {...sharedProps} />;

    default:
      // eslint-disable-next-line no-console
      console.error(`The booking engine doesn't know how to handle the step ${currentStep}`);
      return null;
  }
};

export const BookingStep = (props: Props) => {
  const appContext = useAppContext();
  const dispatch = useDispatch();
  const trackingStorage: any = getTrackingStorage(BE_TRACKING);
  const [infoBannerData, setInfoBannerData] = useState({
    productInformationBanners: [],
  });

  const fetchInfoBannerData = async () => {
    const response = await graphqlClient.runNoCacheQuery(
      getInfoBannerQuery(props.resort.id),
      appContext,
      {
        requestName: WIDGET_PRODUCT_FETCH_INFORMATION_BANNERS,
      },
    );
    setInfoBannerData(response);
  };

  useEffect(() => {
    fetchInfoBannerData();
  }, [props.resort.id]);
  const { currentStep, resort, selectedProposal } = props;
  const router = useRouter();
  const [redirecting, setRedirecting] = useState(false);
  const isChineseLocales = isChineseLocale(appContext.locale);
  const chinaTimerSessionKey = sessionStorage.getItem(getChinaTimerSessionKey(selectedProposal.id));
  const [timerInitialValue, setTimerInitialValue] = useState(
    isChineseLocales ? Number(chinaTimerSessionKey) : 0,
  );
  const showTimer = isChineseLocales && !!selectedProposal?.transports;
  const insurancesStore = useSelector(getInsurancesStore);
  useEffect(() => {
    const storePrice = () =>
      setTrackingStorage({
        ...trackingStorage,
        base_price: {
          ...trackingStorage.base_price,
          proposalId: selectedProposal.id,
          [`base_price_${currentStep}`]: selectedProposal.price.total.value,
        },
      });

    const basePrice = trackingStorage.base_price;

    if (basePrice && basePrice.proposalId !== selectedProposal.id) {
      setTrackingStorage({ ...trackingStorage, base_price: null });
    }

    storePrice();
  }, [currentStep, selectedProposal.id]);
  useEffect(() => {
    if (isEmpty(insurancesStore)) {
      const insurancesInitialState = {};
      selectedProposal.additionalServices.insurance?.forEach((ins) => {
        insurancesInitialState[ins.pricingInfo.id] = {
          isAdded: ins.pricingInfo.isPartiallyRented,
          isDeleted: false,
          termsChecked: false,
        };
      });
      dispatch(createAction(INIT_INSURANCES, insurancesInitialState));
    }
  }, []);

  const redirectToResort = () => {
    const resortUri = new URI(resort?.href)
      .query({ ...omit(router.query, ['bookingStep', 'proposalIds', 'selectedProposalId']) })
      .toString();
    setRedirecting(true);
    router.push(urlUtils.normalizeUrl(resortUri));
  };

  const bookingStepContentProps = {
    ...props,
    setTimerInitialValue,
    showTimer,
    timerInitialValue,
    infoBannerData,
  };
  return (
    <Fragment>
      {showTimer && timerInitialValue > 0 && (
        <div className="font-brand">
          <Countdown
            className="pt-10"
            color={COLORS.ORANGE}
            theme="timer"
            dateTimeEnd={moment.unix(timerInitialValue)}
            description={t('booking.transport.countdown.proposalTimeGuaranty')}
            showTimer={currentStep === 'summary' || currentStep === 'banking'}
            units={['minutes', 'seconds']}
            expiredRender={
              !redirecting && (
                <Modal size="small" hasCloseButton={false}>
                  <div className="rounded-12 text-center">
                    <div className="bg-lightGrey p-10 font-bold">
                      {t('booking.transport.countdown.countdownExpired')}
                    </div>
                    <div className="p-30 font-bold">
                      {`00${t('common.countdownMinute')} 0${t('common.countdownSecond')}`}
                    </div>
                    <div className="pb-20 pt-10">
                      {t('booking.transport.countdown.countdownExpiredInformation')}
                    </div>
                    <Button className="mb-20" onClick={redirectToResort}>
                      {t('booking.transport.countdown.retryNewProposal')}
                    </Button>
                  </div>
                </Modal>
              )
            }
            minutes={t('common.countdownMinute')}
            seconds={t('common.countdownSecond')}
          />
        </div>
      )}
      <BookingStepContent {...bookingStepContentProps} />
    </Fragment>
  );
};
